import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  css,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { WotcSurveyData } from './types';
import { usePostWotcSurvey } from '@features/wotc/hooks/usePostWotcSurvey';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { staticFileLocation } from '@server/constants';
import dayjs from 'dayjs';

type ExFelonFormModalProps = {
  exFelonFormModal: boolean;
  handleClose: () => void;
  wotcSurveyData: WotcSurveyData;
};
import { Dayjs } from 'dayjs';

const defaultValues = {
  criminal1: 'Y',
  criminal1a: null as Dayjs | null,
  criminal1b: null as Dayjs | null,
  criminal1c: '',
  criminal1d: 'N',
};

type ExFelonFormFields = {
  criminal1: string;
  criminal1a: Dayjs | null;
  criminal1b: Dayjs | null;
  criminal1c: string;
  criminal1d: string;
};

const ExFelonFormModal: FC<ExFelonFormModalProps> = ({
  exFelonFormModal,
  handleClose,
  wotcSurveyData,
}) => {
  const [errorText, setErrorText] = useState('');
  const { sendWotcSurvey, isSendingWotcSurvey } = usePostWotcSurvey({
    setErrorText,
  });
  const { handleSubmit, control, watch, setValue, formState: { errors }, } = useForm<ExFelonFormFields>({
    defaultValues,
  });
  const criminal1 = watch('criminal1'); // Watch the value of the first question

  // Function to handle form submission
  const onSubmit = (data: {
    criminal1: any;
    criminal1a: { toISOString: () => string } | null;
    criminal1b: { toISOString: () => string } | null;
    criminal1c: any;
    criminal1d: any;
  }) => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        toast.success('Data submitted successfully');
        handleClose();
      },
    };
    const transformedData = {
      ...data,
      status: 4,
      criminal1a: data.criminal1a?.toISOString().split('T')[0] || null,
      criminal1b: data.criminal1b?.toISOString().split('T')[0] || null,
    };
    sendWotcSurvey({ ...transformedData }, options);
  };

  useEffect(() => {
    if (wotcSurveyData) {
      Object.keys(defaultValues).forEach((key) => {
        const typedKey = key as keyof typeof defaultValues &
          keyof WotcSurveyData;
        if (wotcSurveyData[typedKey] !== undefined) {
          if (typedKey === 'criminal1a' || typedKey === 'criminal1b') {
            const dateValue = wotcSurveyData[typedKey];
            const date = dateValue ? dayjs(dateValue) : null;
            setValue(typedKey, date);
          } else   if (wotcSurveyData[typedKey] !== null) {
            setValue(typedKey, wotcSurveyData[typedKey]);
          }
        }
      });
    }
  }, [wotcSurveyData, setValue]);

  return (
    <Dialog
      open={exFelonFormModal}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', right: 8, top: 8 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <img
            src={`${staticFileLocation}img/images/icon5.png`}
            alt="Logo"
            css={css`
              max-height: 60px;
              display: block;
            `}
          />
          <Typography variant="h5">Criminal Record</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          {errorText && (
            <Alert sx={{ mb: 1 }} severity="error">
              {errorText}
            </Alert>
          )}
          {/* Main Question */}
          <FormControl component="fieldset" fullWidth>
            <Box
              sx={{
                display: 'flex',
                alignItems: {
                  xs: 'start',
                  sm: 'center',
                },
                justifyContent: 'space-between',
                flexDirection: {
                  xs: 'column',
                  sm: 'row',
                },
                gap: '4px',
              }}
            >
              <FormLabel component="legend">
                Were you convicted of a felony or released from prison after a
                felony conviction during the past year?
              </FormLabel>
              <Controller
                name="criminal1"
                control={control}
                rules={{ required: 'This field is required' }}
                render={({ field }) => (
                  <RadioGroup {...field} row sx={{ flexWrap: 'nowrap' }}>
                    <FormControlLabel
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                )}
              />
            </Box>
          </FormControl>

          {/* Conditional Form */}
          {criminal1 === 'Y' && (
            <>
              {/* Date of Conviction */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: {
                    xs: 'column',
                    sm: 'row',
                  },
                  gap: 2,
                  mt: 3,
                }}
              >
                <FormControl fullWidth>
                  <FormLabel>Enter the date of conviction*</FormLabel>
                  <Controller
                    name="criminal1a"
                    control={control}
                    rules={{ required: 'Date of conviction is required' }}
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        onChange={(date) => field.onChange(date)}
                        slotProps={{
                          textField: {
                            error: !!fieldState.error,
                            helperText: fieldState.error?.message,
                          },
                        }}
                      />
                    )}
                  />
                   {errors.criminal1a && (
                    <span
                      style={{ color: 'red', margin: '3px', fontSize: '12px' }}
                    >
                      {errors.criminal1a?.message}
                    </span>
                  )}
                </FormControl>

                {/* Date of Release */}
                <FormControl fullWidth>
                  <FormLabel>Enter the date of release*</FormLabel>
                  <Controller
                    name="criminal1b"
                    control={control}
                    rules={{ required: 'Date of release is required' }}
                    render={({ field, fieldState }) => (
                      <DatePicker
                        {...field}
                        onChange={(date) => field.onChange(date)}
                      />
                    )}
                  />
                   {errors.criminal1b && (
                    <span
                      style={{ color: 'red', margin: '3px', fontSize: '12px' }}
                    >
                      {errors.criminal1b?.message}
                    </span>
                  )}
                </FormControl>
              </Box>

              {/* Federal or State Conviction */}
              <FormControl component="fieldset" fullWidth sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: {
                      xs: 'start',
                      sm: 'center',
                    },
                    justifyContent: 'space-between',
                    flexDirection: {
                      xs: 'column',
                      sm: 'row',
                    },
                    gap: '4px',
                  }}
                >
                  <FormLabel>Was this Federal or State Conviction?</FormLabel>
                  <Controller
                    name="criminal1c"
                    control={control}
                    rules={{ required: 'This field is required' }}
                    render={({ field }) => (
                      <RadioGroup {...field} row>
                        <FormControlLabel
                          value="Federal"
                          control={<Radio />}
                          label="Federal"
                        />
                        <FormControlLabel
                          value="State"
                          control={<Radio />}
                          label="State"
                        />
                      </RadioGroup>
                    )}
                  />
                </Box>
                   {errors.criminal1c && (
                    <span
                      style={{ color: 'red', margin: '3px', fontSize: '12px' }}
                    >
                      {errors.criminal1c?.message}
                    </span>
                  )}
              </FormControl>

              {/* Work Release Program */}
              <FormControl component="fieldset" fullWidth sx={{ mt: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: {
                      xs: 'start',
                      sm: 'center',
                    },
                    justifyContent: 'space-between',
                    flexDirection: {
                      xs: 'column',
                      sm: 'row',
                    },
                    gap: '4px',
                  }}
                >
                  <FormLabel>
                    Are you currently in a work release program?
                  </FormLabel>
                  <Controller
                    name="criminal1d"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field} row>
                        <FormControlLabel
                          value="Y"
                          control={<Radio />}
                          label="Yes"
                        />
                        <FormControlLabel
                          value="N"
                          control={<Radio />}
                          label="No"
                        />
                      </RadioGroup>
                    )}
                  />
                </Box>
              </FormControl>
            </>
          )}

          {/* Submit Button */}
          <Box width="100%" textAlign="center">
            <LoadingButton
              loading={isSendingWotcSurvey}
              sx={{ width: '100%', mt: 3 }}
              type="submit"
              variant="contained"
              color="primary"
            >
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ExFelonFormModal;
